(ns ductile.time.js-types
  "Time/Date types for use on the frontend. This provides the following
  equivalents:

  | #time/date \"2020-10-10\"                      | java.time.LocalDate     | goog.date.Date                  |
  | #time/time \"05:30:45\"                        | java.time.LocalTime     | ductile.time.js-types/LocalTime |
  | #time/date-time \"2020-10-07T12:16:41.761088\" | java.time.LocalDateTime | goog.date.DateTime              |

  For all of these we make sure the tagged reader literals are read correctly,
  and that values of these types print with these reader tags.

  All times are \"local\" i.e. Europe/Berlin time.

  We also implement an ad-hoc API here, adding things as we need them, making
  sure that these functions hide the details of these types. All date/time
  manipulation on the frontend should go through this namespace. Avoid calling
  properties/methods of concrete types directly, and avoid using js/Date.
  "
  (:require [cljs.reader :as reader]
            [clojure.string :as str]
            [goog.date :as gdate]
            [goog.string :as gstr]
            [goog.string.format])
  (:import (goog.date Date DateTime)))

(defprotocol Format
  (format [obj])
  (format-iso [obj]))

(defprotocol Conversions
  (epoch-ms [obj] "Milliseconds since January 1, 1970"))

(deftype LocalTime [hours minutes seconds nanos]
  Format
  (format [obj]
    (gstr/format "%02d:%02d:%02d" hours minutes seconds))
  (format-iso [obj]
    (if nanos
      (gstr/format "%02d:%02d:%02d.%6d" hours minutes seconds (long (/ nanos 1000)))
      (gstr/format "%02d:%02d:%02d" hours minutes seconds)))
  IPrintWithWriter
  (-pr-writer [obj writer _opts]
    (-write writer (str "#time/time " (pr-str (format-iso obj)))))
  Object
  (toString [obj]
    (format-iso obj))
  (valueOf [obj]
    (+ (* (+ seconds (* (+ minutes (* hours 60)) 60)) 10e9) (or nanos 0)))
  IEquiv
  (-equiv [this that]
    (and (instance? LocalTime that)
         (= (.valueOf this) (.valueOf that))))
  IComparable
  (-compare [this that]
    (let [v1 (.valueOf this)
          v2 (.valueOf that)]
      (- v1 v2))))

(defn local-time
  "constructs a LocalTime"
  ([hours minutes seconds]
   (local-time hours minutes seconds nil))
  ([hours minutes seconds nanos]
   (->LocalTime hours minutes seconds nanos)))

(extend-type goog.date.Date
  Format
  (format [obj]
    (gstr/format "%02d.%02d.%04d"
                 (.getDate obj)
                 (inc (.getMonth obj))
                 (.getFullYear obj)))
  (format-iso [obj]
    (gstr/format "%04d-%02d-%02d"
                 (.getFullYear obj)
                 (inc (.getMonth obj))
                 (.getDate obj)))
  Conversions
  (epoch-ms [obj]
    (.getTime obj))
  IPrintWithWriter
  (-pr-writer [obj writer _opts]
    (-write writer (str "#time/date " (pr-str (format-iso obj)))))
  Object
  (toString [obj]
    (format-iso obj))
  IEquiv
  (-equiv [this that]
    (and (instance? goog.date.Date that)
         (= (str this) (str that))))
  IComparable
  (-compare [this that]
    (let [v1 (.valueOf this)
          v2 (.valueOf that)]
      (- v1 v2))))

(defn local-date
  "contstructs a goog.date.Date"
  ([]
   (goog.date.Date.))
  ([year month day]
   (goog.date.Date. year (dec month) day)))

(extend-type DateTime
  Format
  (format [obj]
    (gstr/format "%02d.%02d.%04d, %02d:%02d:%02d"
                 (.getDate obj)
                 (inc (.getMonth obj))
                 (.getFullYear obj)
                 (.getHours obj)
                 (.getMinutes obj)
                 (.getSeconds obj)))
  (format-iso [obj]
    (str
     (gstr/format "%04d-%02d-%02dT%02d:%02d"
                  (.getFullYear obj)
                  (inc (.getMonth obj))
                  (.getDate obj)
                  (.getHours obj)
                  (.getMinutes obj))
     ;; Mimic java.time.LocalDateTime, only print seconds and millis when they
     ;; are not zero
     (when (or (not= 0 (.getSeconds obj)) (not= 0 (.getMilliseconds obj)))
       (gstr/format ":%02d" (.getSeconds obj)))
     (when (not= 0 (.getMilliseconds obj))
       (gstr/format ".%03d" (.getMilliseconds obj)))))
  Conversions
  (epoch-ms [obj]
    (.getTime obj))
  IPrintWithWriter
  (-pr-writer [obj writer _opts]
    (-write writer (str "#time/date-time " (pr-str (format-iso obj)))))
  Object
  (toString [obj]
    (format-iso obj))
  IEquiv
  (-equiv [this that]
    (and (instance? goog.date.DateTime that)
         (= (str this) (str that))))
  IComparable
  (-compare [this that]
    (let [v1 (.valueOf this)
          v2 (.valueOf that)]
      (- v1 v2))))

(extend-type js/Date
  Conversions
  (epoch-ms [obj]
    (.getTime obj)))

;; The way goog.date.Date/DateTime are defined means the constructor function is
;; anonymous, it's name is "", and this property is by default read-only. We
;; make it writable and set it, so that Puget can inspect it.
(js/Object.defineProperty goog.date.Date "name" #js {:writable true})
(js/Object.defineProperty goog.date.DateTime "name" #js {:writable true})
(set! (.-name goog.date.Date) "goog.date.Date")
(set! (.-name goog.date.DateTime) "goog.date.DateTime")

(defn local-date-time
  "contstructs a goog.date.DateTime"
  ([]
   (goog.date.DateTime.))
  ([year month day hours minutes seconds nanos]
   (goog.date.DateTime. year (dec month) day hours minutes seconds (long (/ nanos 10e5)))))

(defn parse-local-date
  "Parse a date (YYYY-MM-DD) to a goog.date.Date"
  [yyyy-mm-dd]
  (Date/fromIsoString yyyy-mm-dd))

(defn parse-local-time
  "Parse a timestamp (HH:MM:SS or HH:MM:SS.mmm or HH:MM:SS.nnnnnn) to a LocalTime."
  [time]
  (let [hours   (js/parseInt (subs time 0 2) 10)
        minutes (js/parseInt (subs time 3 5) 10)
        seconds (if (> (count time) 5)
                  (js/parseInt (subs time 6 8) 10)
                  0)
        nanos   (when (= \. (.charAt time 8))
                  (subs time 9))
        nanos   (when nanos
                  (* (js/parseInt nanos 10) (Math/pow 10 (- 9 (count nanos)))))]
    (->LocalTime hours minutes seconds nanos)))

(defn parse-local-date-time
  "Parse an ISO timestamp without time zone information to a goog.date.DateTime.
  We implement our own parsing logic for the time section, to prevent time zone
  shifting. The numbers you provide in the timestamp are the numbers that end up
  in the DateTime. If we leave it to goog.date then it will assume the timestamp
  is UTC, and shift it to the browser's timezone."
  [date-time]
  (let [d           (goog.date.DateTime. 2000)
        delim       (if (= -1 (.indexOf date-time "T")) " " "T")
        [date time] (str/split date-time delim)
        d           (when (gdate/setIso8601DateOnly_ d date) d)
        t           (parse-local-time time)]
    (when d
      (.setHours d (.-hours t))
      (.setMinutes d (.-minutes t))
      (.setSeconds d (.-seconds t))
      ;; goog.date.DateTime only has milliseconds, so we truncate. Not a huge
      ;; loss.
      (.setMilliseconds d (long (/ (.-nanos t) 10e5)))
      d)))

(defn parse-instant [date-time]
  (js/Date. date-time))

(defn parse-offset-date-time
  "Parse an ISO timestamp with timezone offset (Z or +/-) to a goog.date.DateTime.
  Note that the result is essentially a LocalDateTime, since none of the types
  at our disposal on the frontend actually store a timezone or offset. Instead
  goog.date's parsing logic will shift the time based on the difference between
  the time zone in the timestamp, and the timezone the browser is in."
  [date-time]
  (DateTime/fromIsoString date-time))

(defn current-time-millis []
  (js/Date.now))

(defn today []
  (goog.date.Date.))

(defn now []
  (goog.date.DateTime.))

(defn yyyy-mm-dd [d]
  (subs (format-iso d) 0 (count "yyyy-MM-dd")))

(defn days-between [begin end]
  (let [begin-ms (epoch-ms begin)
        end-ms (epoch-ms end)
        days (js/Math.ceil (/ (- end-ms begin-ms)
                              (* 60 60 1000 24)))]
    (cond
      (< days 0) (js/Math.floor days)
      (< 0 days) (js/Math.ceil days)
      :else 0)))

(defn same-day? [d1 d2]
  (zero? (days-between d1 d2)))

(defn today? [d1]
  (same-day? d1 (today)))

(def data-readers
  {'time/date parse-local-date
   'time/date-time parse-local-date-time
   'time/time parse-local-time})

(doseq [[tag cb] data-readers] (reader/register-tag-parser! tag cb))
